// Global Types
import type {
  $Config as $ApiServiceConfig,
} from '@vroskus/library-api';
import type {
  $WebRouteRequest as $ApiRouteRequest,
  $WebRouteResponse as $ApiRouteResponse,
} from 'common.types';

// Helpers
import Api from '@vroskus/library-api';
import {
  z,
} from 'zod';

// Schemas
const objectSchema = z.object({
}).passthrough();
const arraySchema = z.array(objectSchema);
const objectOrNullSchema = objectSchema.nullable();

// Types
export type $Config = $ApiServiceConfig;

class ApiService<C extends $ApiServiceConfig> extends Api<C> {
  // Auth
  async authenticate(
    params: $ApiRouteRequest<'/auth/authenticate'>['body'],
  ): Promise<$ApiRouteResponse<'/auth/authenticate'>> {
    const response = await this.connection.post(
      '/auth/authenticate',
      params,
    );

    objectSchema.parse(response.data);

    return response.data;
  }

  async unauthenticate(): Promise<$ApiRouteResponse<'/auth/unauthenticate'>> {
    await this.connection.post('/auth/unauthenticate');
  }

  async invalidateAccessToken(
    params: $ApiRouteRequest<'/auth/invalidate_access_token'>['body'],
  ): Promise<$ApiRouteResponse<'/auth/invalidate_access_token'>> {
    const response = await this.connection.post(
      '/auth/invalidate_access_token',
      params,
    );

    return response.data;
  }

  async getAuthUser(): Promise<$ApiRouteResponse<'/auth/user/get_one'>> {
    const response = await this.connection.post('/auth/user/get_one');

    return response.data;
  }

  async updateAuthUser(
    params: $ApiRouteRequest<'/auth/user/update_one'>['body'],
  ): Promise<$ApiRouteResponse<'/auth/user/update_one'>> {
    const response = await this.connection.post(
      '/auth/user/update_one',
      params,
    );

    return response.data;
  }

  async deleteAuthUser(): Promise<$ApiRouteResponse<'/auth/user/delete_one'>> {
    const response = await this.connection.post('/auth/user/delete_one');

    return response.data;
  }

  // Regions
  async getRegions(): Promise<$ApiRouteResponse<'/regions'>> {
    const response = await this.connection.get('/regions');

    arraySchema.parse(response.data);

    return response.data;
  }

  // Championships page
  async getChampionshipPageData(
    params: $ApiRouteRequest<'/page/championship'>['body'],
  ): Promise<$ApiRouteResponse<'/page/championship'>> {
    const response = await this.connection.post(
      '/page/championship',
      params,
    );

    objectOrNullSchema.parse(response.data);

    return response.data;
  }

  async getChampionshipBannersSectionData(
    params: $ApiRouteRequest<'/page/championship/section/banners'>['body'],
  ): Promise<$ApiRouteResponse<'/page/championship/section/banners'>> {
    const response = await this.connection.post(
      '/page/championship/section/banners',
      params,
    );

    arraySchema.parse(response.data);

    return response.data;
  }

  async getChampionshipDocumentsSectionData(
    params: $ApiRouteRequest<'/page/championship/section/documents'>['body'],
  ): Promise<$ApiRouteResponse<'/page/championship/section/documents'>> {
    const response = await this.connection.post(
      '/page/championship/section/documents',
      params,
    );

    arraySchema.parse(response.data);

    return response.data;
  }

  async getChampionshipRegattasSectionData(
    params: $ApiRouteRequest<'/page/championship/section/regattas'>['body'],
  ): Promise<$ApiRouteResponse<'/page/championship/section/regattas'>> {
    const response = await this.connection.post(
      '/page/championship/section/regattas',
      params,
    );

    arraySchema.parse(response.data);

    return response.data;
  }

  async getChampionshipResultsSectionData(
    params: $ApiRouteRequest<'/page/championship/section/results'>['body'],
  ): Promise<$ApiRouteResponse<'/page/championship/section/results'>> {
    const response = await this.connection.post(
      '/page/championship/section/results',
      params,
    );

    arraySchema.parse(response.data);

    return response.data;
  }

  async getChampionshipSponsorsSectionData(
    params: $ApiRouteRequest<'/page/championship/section/sponsors'>['body'],
  ): Promise<$ApiRouteResponse<'/page/championship/section/sponsors'>> {
    const response = await this.connection.post(
      '/page/championship/section/sponsors',
      params,
    );

    arraySchema.parse(response.data);

    return response.data;
  }

  // Custom page
  async getCustomPagePageData(
    params: $ApiRouteRequest<'/page/custom'>['body'],
  ): Promise<$ApiRouteResponse<'/page/custom'>> {
    const response = await this.connection.post(
      '/page/custom',
      params,
    );

    objectOrNullSchema.parse(response.data);

    return response.data;
  }

  // Regattas page
  async getPageData(
    params: $ApiRouteRequest<'/page'>['body'],
  ): Promise<$ApiRouteResponse<'/page'>> {
    const response = await this.connection.post(
      '/page',
      params,
    );

    objectOrNullSchema.parse(response.data);

    return response.data;
  }

  async getRegattasPageData(
    params: $ApiRouteRequest<'/page/regattas'>['body'],
  ): Promise<$ApiRouteResponse<'/page/regattas'>> {
    const response = await this.connection.post(
      '/page/regattas',
      params,
    );

    objectOrNullSchema.parse(response.data);

    return response.data;
  }

  async getRegattasAdvertisementsSectionData(
    params: $ApiRouteRequest<'/page/regattas/section/advertisements'>['body'],
  ): Promise<$ApiRouteResponse<'/page/regattas/section/advertisements'>> {
    const response = await this.connection.post(
      '/page/regattas/section/advertisements',
      params,
    );

    arraySchema.parse(response.data);

    return response.data;
  }

  async getRegattasBannersSectionData(
    params: $ApiRouteRequest<'/page/regattas/section/banners'>['body'],
  ): Promise<$ApiRouteResponse<'/page/regattas/section/banners'>> {
    const response = await this.connection.post(
      '/page/regattas/section/banners',
      params,
    );

    arraySchema.parse(response.data);

    return response.data;
  }

  async getRegattasEntriesSectionData(
    params: $ApiRouteRequest<'/page/regattas/section/entries'>['body'],
  ): Promise<$ApiRouteResponse<'/page/regattas/section/entries'>> {
    const response = await this.connection.post(
      '/page/regattas/section/entries',
      params,
    );

    arraySchema.parse(response.data);

    return response.data;
  }

  async getRegattasRegattasSectionData(
    params: $ApiRouteRequest<'/page/regattas/section/regattas'>['body'],
  ): Promise<$ApiRouteResponse<'/page/regattas/section/regattas'>> {
    const response = await this.connection.post(
      '/page/regattas/section/regattas',
      params,
    );

    z.object({
      Locations: arraySchema,
      Regattas: arraySchema,
    }).parse(response.data);

    return response.data;
  }

  // Championships page
  async getChampionshipsPageData(
    params: $ApiRouteRequest<'/page/championships'>['body'],
  ): Promise<$ApiRouteResponse<'/page/championships'>> {
    const response = await this.connection.post(
      '/page/championships',
      params,
    );

    objectOrNullSchema.parse(response.data);

    return response.data;
  }

  async getChampionshipsBannersSectionData(
    params: $ApiRouteRequest<'/page/championships/section/banners'>['body'],
  ): Promise<$ApiRouteResponse<'/page/championships/section/banners'>> {
    const response = await this.connection.post(
      '/page/championships/section/banners',
      params,
    );

    arraySchema.parse(response.data);

    return response.data;
  }

  async getChampionshipsChampionshipsSectionData(
    params: $ApiRouteRequest<'/page/championships/section/championships'>['body'],
  ): Promise<$ApiRouteResponse<'/page/championships/section/championships'>> {
    const response = await this.connection.post(
      '/page/championships/section/championships',
      params,
    );

    arraySchema.parse(response.data);

    return response.data;
  }

  // Profile page
  async getProfileAdvertisementsSectionData(
    params: $ApiRouteRequest<'/page/profile/section/advertisements'>['body'],
  ): Promise<$ApiRouteResponse<'/page/profile/section/advertisements'>> {
    const response = await this.connection.post(
      '/page/profile/section/advertisements',
      params,
    );

    arraySchema.parse(response.data);

    return response.data;
  }

  async getProfileRegattaNotificationsSectionData(
    params: $ApiRouteRequest<'/page/profile/section/regatta_notifications'>['body'],
  ): Promise<$ApiRouteResponse<'/page/profile/section/regatta_notifications'>> {
    const response = await this.connection.post(
      '/page/profile/section/regatta_notifications',
      params,
    );

    arraySchema.parse(response.data);

    return response.data;
  }

  async getProfileRegattaEntriesSectionData(
    params: $ApiRouteRequest<'/page/profile/section/regatta_entries'>['body'],
  ): Promise<$ApiRouteResponse<'/page/profile/section/regatta_entries'>> {
    const response = await this.connection.post(
      '/page/profile/section/regatta_entries',
      params,
    );

    arraySchema.parse(response.data);

    return response.data;
  }

  async getProfileSessionSectionData(): Promise<$ApiRouteResponse<'/page/profile/section/session'>> {
    const response = await this.connection.post('/page/profile/section/session');

    arraySchema.parse(response.data);

    return response.data;
  }

  // Regatta page
  async getRegattaPageData(
    params: $ApiRouteRequest<'/page/regatta'>['body'],
  ): Promise<$ApiRouteResponse<'/page/regatta'>> {
    const response = await this.connection.post(
      '/page/regatta',
      params,
    );

    objectOrNullSchema.parse(response.data);

    return response.data;
  }

  async getRegattaAdvertisementsSectionData(
    params: $ApiRouteRequest<'/page/regatta/section/advertisements'>['body'],
  ): Promise<$ApiRouteResponse<'/page/regatta/section/advertisements'>> {
    const response = await this.connection.post(
      '/page/regatta/section/advertisements',
      params,
    );

    arraySchema.parse(response.data);

    return response.data;
  }

  async getRegattaBannersSectionData(
    params: $ApiRouteRequest<'/page/regatta/section/banners'>['body'],
  ): Promise<$ApiRouteResponse<'/page/regatta/section/banners'>> {
    const response = await this.connection.post(
      '/page/regatta/section/banners',
      params,
    );

    arraySchema.parse(response.data);

    return response.data;
  }

  async getRegattaBoatRentsSectionData(
    params: $ApiRouteRequest<'/page/regatta/section/boat_rents'>['body'],
  ): Promise<$ApiRouteResponse<'/page/regatta/section/boat_rents'>> {
    const response = await this.connection.post(
      '/page/regatta/section/boat_rents',
      params,
    );

    arraySchema.parse(response.data);

    return response.data;
  }

  async getRegattaChampionshipsSectionData(
    params: $ApiRouteRequest<'/page/regatta/section/championships'>['body'],
  ): Promise<$ApiRouteResponse<'/page/regatta/section/championships'>> {
    const response = await this.connection.post(
      '/page/regatta/section/championships',
      params,
    );

    arraySchema.parse(response.data);

    return response.data;
  }

  async getRegattaContactsSectionData(
    params: $ApiRouteRequest<'/page/regatta/section/contacts'>['body'],
  ): Promise<$ApiRouteResponse<'/page/regatta/section/contacts'>> {
    const response = await this.connection.post(
      '/page/regatta/section/contacts',
      params,
    );

    arraySchema.parse(response.data);

    return response.data;
  }

  async getRegattaDocumentsSectionData(
    params: $ApiRouteRequest<'/page/regatta/section/documents'>['body'],
  ): Promise<$ApiRouteResponse<'/page/regatta/section/documents'>> {
    const response = await this.connection.post(
      '/page/regatta/section/documents',
      params,
    );

    arraySchema.parse(response.data);

    return response.data;
  }

  async getRegattaEntriesSectionData(
    params: $ApiRouteRequest<'/page/regatta/section/entries'>['body'],
  ): Promise<$ApiRouteResponse<'/page/regatta/section/entries'>> {
    const response = await this.connection.post(
      '/page/regatta/section/entries',
      params,
    );

    arraySchema.parse(response.data);

    return response.data;
  }

  async getRegattaMessagesSectionData(
    params: $ApiRouteRequest<'/page/regatta/section/messages'>['body'],
  ): Promise<$ApiRouteResponse<'/page/regatta/section/messages'>> {
    const response = await this.connection.post(
      '/page/regatta/section/messages',
      params,
    );

    arraySchema.parse(response.data);

    return response.data;
  }

  async getRegattaProtestsSectionData(
    params: $ApiRouteRequest<'/page/regatta/section/protests'>['body'],
  ): Promise<$ApiRouteResponse<'/page/regatta/section/protests'>> {
    const response = await this.connection.post(
      '/page/regatta/section/protests',
      params,
    );

    z.object({
      RegattaDays: arraySchema,
      RegattaProtests: arraySchema,
    }).parse(response.data);

    return response.data;
  }

  async getRegattaRegistrationSectionData(
    params: $ApiRouteRequest<'/page/regatta/section/registration'>['body'],
  ): Promise<$ApiRouteResponse<'/page/regatta/section/registration'>> {
    const response = await this.connection.post(
      '/page/regatta/section/registration',
      params,
    );

    objectOrNullSchema.parse(response.data);

    return response.data;
  }

  async getRegattaResultsOverallSectionData(
    params: $ApiRouteRequest<'/page/regatta/section/results_overall'>['body'],
  ): Promise<$ApiRouteResponse<'/page/regatta/section/results_overall'>> {
    const response = await this.connection.post(
      '/page/regatta/section/results_overall',
      params,
    );

    arraySchema.parse(response.data);

    return response.data;
  }

  async getRegattaResultsSectionData(
    params: $ApiRouteRequest<'/page/regatta/section/results'>['body'],
  ): Promise<$ApiRouteResponse<'/page/regatta/section/results'>> {
    const response = await this.connection.post(
      '/page/regatta/section/results',
      params,
    );

    arraySchema.parse(response.data);

    return response.data;
  }

  async getRegattaSchedulesSectionData(
    params: $ApiRouteRequest<'/page/regatta/section/schedules'>['body'],
  ): Promise<$ApiRouteResponse<'/page/regatta/section/schedules'>> {
    const response = await this.connection.post(
      '/page/regatta/section/schedules',
      params,
    );

    arraySchema.parse(response.data);

    return response.data;
  }

  async getRegattaSponsorsSectionData(
    params: $ApiRouteRequest<'/page/regatta/section/sponsors'>['body'],
  ): Promise<$ApiRouteResponse<'/page/regatta/section/sponsors'>> {
    const response = await this.connection.post(
      '/page/regatta/section/sponsors',
      params,
    );

    arraySchema.parse(response.data);

    return response.data;
  }

  async getRegattaTrackerSectionData(
    params: $ApiRouteRequest<'/page/regatta/section/tracker'>['body'],
  ): Promise<$ApiRouteResponse<'/page/regatta/section/tracker'>> {
    const response = await this.connection.post(
      '/page/regatta/section/tracker',
      params,
    );

    z.object({
      RegattaCourseRaces: arraySchema,
      RegattaTrackers: arraySchema,
    }).parse(response.data);

    return response.data;
  }

  // Regatta
  async getResourceRegatta(
    params: $ApiRouteRequest<'/data/regatta/get_resource'>['body'],
  ): Promise<$ApiRouteResponse<'/data/regatta/get_resource'>> {
    const response = await this.connection.post(
      '/data/regatta/get_resource',
      params,
    );

    arraySchema.parse(response.data);

    return response.data;
  }

  // Entry
  async findManyEntry(
    params: $ApiRouteRequest<'/data/entry/find_many'>['body'],
  ): Promise<$ApiRouteResponse<'/data/entry/find_many'>> {
    const response = await this.connection.post(
      '/data/entry/find_many',
      params,
    );

    return response.data;
  }

  // RegattaEntry
  async findManyRegattaEntry(
    params: $ApiRouteRequest<'/data/regatta_entry/find_many'>['body'],
  ): Promise<$ApiRouteResponse<'/data/regatta_entry/find_many'>> {
    const response = await this.connection.post(
      '/data/regatta_entry/find_many',
      params,
    );

    return response.data;
  }

  async getManyRegattaEntryByCrewmember(
    params: $ApiRouteRequest<'/data/regatta_entry/get_many_by_crewmember'>['body'],
  ): Promise<$ApiRouteResponse<'/data/regatta_entry/get_many_by_crewmember'>> {
    const response = await this.connection.post(
      '/data/regatta_entry/get_many_by_crewmember',
      params,
    );

    return response.data;
  }

  async getOneRegattaEntryByRegistrationNo(
    params: $ApiRouteRequest<'/data/regatta_entry/get_one_by_registration_no'>['body'],
  ): Promise<$ApiRouteResponse<'/data/regatta_entry/get_one_by_registration_no'>> {
    const response = await this.connection.post(
      '/data/regatta_entry/get_one_by_registration_no',
      params,
    );

    return response.data;
  }

  async getOneRegattaEntryByUid(
    params: $ApiRouteRequest<'/data/regatta_entry/get_one_by_uid'>['body'],
  ): Promise<$ApiRouteResponse<'/data/regatta_entry/get_one_by_uid'>> {
    const response = await this.connection.post(
      '/data/regatta_entry/get_one_by_uid',
      params,
    );

    return response.data;
  }

  async getOneRegattaEntryByCrewmember(
    params: $ApiRouteRequest<'/data/regatta_entry/get_one_by_crewmember'>['body'],
  ): Promise<$ApiRouteResponse<'/data/regatta_entry/get_one_by_crewmember'>> {
    const response = await this.connection.post(
      '/data/regatta_entry/get_one_by_crewmember',
      params,
    );

    return response.data;
  }

  async upsertRegattaEntryData(
    params: $ApiRouteRequest<'/data/regatta_entry/upsert'>['body'],
  ): Promise<$ApiRouteResponse<'/data/regatta_entry/upsert'>> {
    const response = await this.connection.post(
      '/data/regatta_entry/upsert',
      params,
    );

    return response.data;
  }

  async deleteRegattaEntryData(
    params: $ApiRouteRequest<'/data/regatta_entry/delete'>['body'],
  ): Promise<$ApiRouteResponse<'/data/regatta_entry/delete'>> {
    const response = await this.connection.post(
      '/data/regatta_entry/delete',
      params,
    );

    return response.data;
  }

  // RegattaEntryCrewmember
  async findManyRegattaEntryCrewmemberData(
    params: $ApiRouteRequest<'/data/regatta_entry_crewmember/find_many'>['body'],
  ): Promise<$ApiRouteResponse<'/data/regatta_entry_crewmember/find_many'>> {
    const response = await this.connection.post(
      '/data/regatta_entry_crewmember/find_many',
      params,
    );

    return response.data;
  }

  async getAllRegattaEntryCrewmemberData(
    params: $ApiRouteRequest<'/data/regatta_entry_crewmember/get_all'>['body'],
  ): Promise<$ApiRouteResponse<'/data/regatta_entry_crewmember/get_all'>> {
    const response = await this.connection.post(
      '/data/regatta_entry_crewmember/get_all',
      params,
    );

    return response.data;
  }

  // RegattaEntryRequirement
  async getRegattaEntryRequirementData(
    params: $ApiRouteRequest<'/data/regatta_entry_requirement/get'>['body'],
  ): Promise<$ApiRouteResponse<'/data/regatta_entry_requirement/get'>> {
    const response = await this.connection.post(
      '/data/regatta_entry_requirement/get',
      params,
    );

    return response.data;
  }

  // RegattaProtest
  async upsertRegattaProtestData(
    params: $ApiRouteRequest<'/data/regatta_protest/upsert'>['body'],
  ): Promise<$ApiRouteResponse<'/data/regatta_protest/upsert'>> {
    const response = await this.connection.post(
      '/data/regatta_protest/upsert',
      params,
    );

    return response.data;
  }

  // Advertisement
  async getAdvertisementData(
    params: $ApiRouteRequest<'/data/advertisement/get'>['body'],
  ): Promise<$ApiRouteResponse<'/data/advertisement/get'>> {
    const response = await this.connection.post(
      '/data/advertisement/get',
      params,
    );

    return response.data;
  }

  async upsertAdvertisementData(
    params: $ApiRouteRequest<'/data/advertisement/upsert'>['body'],
  ): Promise<$ApiRouteResponse<'/data/advertisement/upsert'>> {
    const response = await this.connection.post(
      '/data/advertisement/upsert',
      params,
    );

    return response.data;
  }

  // RegattaSubscription
  async getRegattaSubscriptionData(
    params: $ApiRouteRequest<'/data/regatta_subscription/get'>['body'],
  ): Promise<$ApiRouteResponse<'/data/regatta_subscription/get'>> {
    const response = await this.connection.post(
      '/data/regatta_subscription/get',
      params,
    );

    return response.data;
  }

  async toggleRegattaSubscriptionData(
    params: $ApiRouteRequest<'/data/regatta_subscription/toggle'>['body'],
  ): Promise<$ApiRouteResponse<'/data/regatta_subscription/toggle'>> {
    const response = await this.connection.post(
      '/data/regatta_subscription/toggle',
      params,
    );

    return response.data;
  }
}

export default ApiService;
